import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  Injector,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  runInInjectionContext,
} from '@angular/core';
import { provideLottieOptions } from 'ngx-lottie';
import { TitleStrategy, provideRouter } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { LUCIDE_ICONS, LucideIconProvider } from 'lucide-angular';
import { AUTH_TOKEN_STORAGE } from '@core/auth/auth-token-storage';
import { AuthTokenService } from '@core/auth/auth-token.service';
import { HybridAuthTokenStorage } from '@core/auth/hybrid-auth-token.storage';
import { SessionConflictService } from '@core/auth/session-conflict.service';
import { API_URL } from '@core/config/api-url.token';
import { RuntimeConfigService } from '@core/config/runtime-config.service';
import { devAttributionInterceptor } from '@core/http/dev-attribution.interceptor';
import { errorNotificationInterceptor } from '@core/http/error-notification.interceptor';
import { preloadActiveTranslation, translocoConfig } from '@core/i18n/transloco.config';
import { telemetryInterceptor } from '@core/telemetry/telemetry.interceptor';
import { provideTelemetry } from '@core/telemetry/telemetry.providers';
import { provideAuthData } from '@features/shared/auth/data/auth-data.providers';
import { ACCOUNT_REALM } from '@core/config/account-realm.token';
import { authInterceptor } from '@features/shared/auth/data/interceptors/auth.interceptor';
import { TokenRefreshSchedulerService } from '@features/shared/auth/data/services/token-refresh-scheduler.service';
import { AccountPresenceHeartbeatService } from '@features/shared/auth/data/services/account-presence-heartbeat.service';
import { AuthStore } from '@features/shared/auth/presentation/store/auth.store';
import { providePasskeysData } from '@features/shared/passkeys/data/passkeys-data.providers';
import { provideAccessRequestData } from '@features/shared/access-requests/data/access-request-data.providers';
import { provideChatData } from '@features/shared/chat/data/chat-data.providers';
import { provideCommunicationData } from '@features/web/communication/data/communication-data.providers';
import { provideClientsData } from '@features/web/clients/data/clients-data.providers';
import { providePatientsData } from '@features/web/patients/data/patients-data.providers';
import { provideDepartmentsData } from '@features/shared/departments/data/departments-data.providers';
import { provideStaffClinicsData } from '@features/shared/clinics/data/staff-clinics-data.providers';
import { provideAnalyticsData } from '@features/web/analytics/data/analytics-data.providers';
import { provideAppointmentsData } from '@features/web/appointments/data/appointments-data.providers';
import { provideClinicalRequestsData } from '@features/web/clinical-requests/data/clinical-requests-data.providers';
import { provideMarketingData } from '@features/web/marketing/data/marketing-data.providers';
import { provideSettingsData } from '@features/web/settings/data/settings-data.providers';
import { providePlatformBillingData } from '@features/web/platform-billing/data/platform-billing-data.providers';
import { provideDashboardData } from '@features/web/dashboard/data/dashboard-data.providers';
import { provideFeedbackData } from '@features/shared/feedback/data/feedback-data.providers';
import { provideBillingData } from '@features/web/billing/data/billing-data.providers';
import { provideOnboardingData } from '@features/web/onboarding/data/onboarding-data.providers';
import { provideProvetExplorerData } from '@features/web/provet-explorer/data/provet-explorer-data.providers';
import { provideProvetRateLimitsData } from '@features/web/provet-rate-limits/data/provet-rate-limits-data.providers';
import { provideDeveloperContextData } from '@features/web/developer-context/data/developer-context-data.providers';
import { provideDocumentationData } from '@features/web/documentation/data/documentation-data.providers';
import { LazyRouteChunkRecoveryService } from '@shared/routing/lazy-route-chunk-recovery.service';
import { webRoutes } from './web-app.routes';
import { ACCOUNT_DELETION_REPOSITORY } from '@features/shared/auth/domain/repositories/account-deletion.repository';
import { AccountDeletionRepository } from '@features/shared/auth/data/repositories/account-deletion.repository.impl';
import { isPublicWebPath } from './web-public-route.util';
import { WebLanguagePreferenceService } from './web-language-preference.service';
import { WebPageTitleStrategy } from './web-page-title.strategy';
import { WEB_LUCIDE_ICONS } from './web-lucide-icons.generated';

export const webAppConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideLottieOptions({ player: () => import('lottie-web') }),
    { provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(WEB_LUCIDE_ICONS) },
    // telemetryInterceptor goes before errorNotificationInterceptor so its timing wraps every other
    // interceptor in the chain.
    provideHttpClient(
      withInterceptors([
        devAttributionInterceptor,
        telemetryInterceptor,
        errorNotificationInterceptor,
        authInterceptor,
      ]),
    ),
    provideRouter(webRoutes),
    { provide: TitleStrategy, useClass: WebPageTitleStrategy },
    provideTelemetry('web'),
    translocoConfig,
    provideAuthData(),
    { provide: ACCOUNT_DELETION_REPOSITORY, useClass: AccountDeletionRepository },
    { provide: ACCOUNT_REALM, useValue: 'administration' },
    providePasskeysData(),
    provideAccessRequestData(),
    provideChatData(),
    provideCommunicationData(),
    provideClientsData(),
    providePatientsData(),
    provideDepartmentsData(),
    provideStaffClinicsData(),
    provideAppointmentsData(),
    provideClinicalRequestsData(),
    provideMarketingData(),
    provideSettingsData(),
    providePlatformBillingData(),
    provideDashboardData(),
    provideFeedbackData(),
    provideAnalyticsData(),
    provideBillingData(),
    provideOnboardingData(),
    provideProvetExplorerData(),
    provideProvetRateLimitsData(),
    provideDeveloperContextData(),
    provideDocumentationData(),
    HybridAuthTokenStorage,
    { provide: AUTH_TOKEN_STORAGE, useExisting: HybridAuthTokenStorage },
    {
      provide: API_URL,
      useFactory: () => inject(RuntimeConfigService).apiBaseUrl,
    },
    provideAppInitializer(() => {
      inject(LazyRouteChunkRecoveryService).start();
    }),
    provideAppInitializer(async () => {
      const injector = inject(Injector);
      const runtimeConfig = inject(RuntimeConfigService);
      await runtimeConfig.load();

      // Deferred until after load(): constructing AuthStore/AuthTokenService pulls in
      // AuthRemoteDatasource, which captures API_URL as a singleton on first injection.
      const transloco = runInInjectionContext(injector, () => inject(TranslocoService));
      await preloadActiveTranslation(transloco);
      if (isPublicWebPath(window.location.pathname)) return;
      const authTokenService = runInInjectionContext(injector, () => inject(AuthTokenService));
      const authStore = runInInjectionContext(injector, () => inject(AuthStore));
      await authTokenService.initialize();
      await authStore.hydrate();
      runInInjectionContext(injector, () => inject(WebLanguagePreferenceService));
      runInInjectionContext(injector, () => inject(TokenRefreshSchedulerService)); // eager: starts the scheduler effect
      runInInjectionContext(injector, () => inject(AccountPresenceHeartbeatService));
      // eager: starts the `storage` event listener (multi-tab session-conflict warning) -- web
      // only, since `HybridAuthTokenStorage`'s shared-`localStorage` "remember me" mode (the thing
      // this service watches for) only exists in the web app; mobile uses `SecureAuthTokenStorage`
      // (native secure storage, not shared across "tabs" the way a browser origin is).
      runInInjectionContext(injector, () => inject(SessionConflictService));
    }),
  ],
};
